import { useEffect, useRef, useState, type ReactNode } from "react";
import { useT } from "./i18n";

type Heading = { id: string; text: string; level: 2 | 3 };

/** "Zašto bravi može da se veruje" -> "zasto-bravi-moze-da-se-veruje": a stable anchor per heading. */
function slug(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * A long reading page (the guide, the terms): a table of contents on the left that follows the
 * reader, the text on the right. Headings get their anchors here, from their own text, so the
 * content files stay plain JSX; the entry of the section on screen is marked as the reader scrolls.
 */
export function Doc({ children }: { children: ReactNode }) {
  const t = useT();
  const article = useRef<HTMLElement>(null);
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const root = article.current;
    if (!root) return;
    const seen = new Map<string, number>();
    const found: Heading[] = [];
    for (const h of root.querySelectorAll<HTMLHeadingElement>("h2, h3")) {
      const text = h.textContent?.trim() ?? "";
      let id = slug(text) || "section";
      const n = seen.get(id) ?? 0;
      seen.set(id, n + 1);
      if (n) id = `${id}-${n + 1}`;
      h.id = id;
      found.push({ id, text, level: h.tagName === "H2" ? 2 : 3 });
    }
    setHeadings(found);
    // The heading closest to the top of the window is the section being read.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive((visible[0].target as HTMLElement).id);
      },
      { rootMargin: "-10% 0px -75% 0px", threshold: 0 },
    );
    for (const h of root.querySelectorAll("h2, h3")) observer.observe(h);
    // A link with #anchor opened directly: scroll to it once the ids exist.
    if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView();
    return () => observer.disconnect();
  }, [children]);

  return (
    <main className="doc">
      <aside className="toc">
        <nav aria-label={t.guide.contents}>
          <p className="toc-title">{t.guide.contents}</p>
          <ul>
            {headings.map((h) => (
              <li key={h.id} className={`lvl${h.level}${h.id === active ? " active" : ""}`}>
                <a href={`#${h.id}`}>{h.text}</a>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
      <article ref={article}>{children}</article>
    </main>
  );
}

/** A link to another site, opened in a new tab. */
export function Ext({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}
